import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import api from '../../api';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const PAGE_WIDTH = 1000;

const lineHoverStyle = `
  .react-pdf__Page__textContent span:hover {
    background-color: rgba(255, 230, 0, 0.3);
    cursor: pointer;
  }
`;

export default function TeamLeaderManualPage() {
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isTeamLeader = currentUser?.position === "팀장";

  const [manual, setManual] = useState(null);
  const [comments, setComments] = useState([]);
  const [numPages, setNumPages] = useState(0);
  const [selection, setSelection] = useState(null); // { text, page, y }
  const [commentDraft, setCommentDraft] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pageTopOffsets, setPageTopOffsets] = useState({}); // { pageNumber: offsetTop relative to scrollContainer }
  const [highlightId, setHighlightId] = useState(null);

  const scrollContainerRef = useRef(null);
  const pageWrapperRefs = useRef({});
  const commentRefs = useRef({});

  useEffect(() => {
    fetchCurrentManual();
  }, []);

  const fetchCurrentManual = async () => {
    const res = await api.get('/manual-guide/manuals/current/');
    if (res.data) {
      setManual(res.data);
      fetchComments(res.data.id);
    }
  };

  const fetchComments = async (manualId) => {
    const res = await api.get(`/manual-guide/manual-comments/?manual=${manualId}`);
    setComments(res.data);
  };

  // 각 페이지 wrapper의, 스크롤 컨테이너 기준 상단 위치를 측정 (사이드바 정렬용)
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const offsets = {};
    Object.entries(pageWrapperRefs.current).forEach(([pageNumber, el]) => {
      if (el) offsets[pageNumber] = el.offsetTop;
    });
    setPageTopOffsets(offsets);
  }, [numPages, manual]);

  const getRelativeY = (rect, pageNumber) => {
    const wrapper = pageWrapperRefs.current[pageNumber];
    if (!wrapper) return 0;
    const wrapperRect = wrapper.getBoundingClientRect();
    return rect.top - wrapperRect.top;
  };

  const handleTextSelect = (pageNumber) => {
    const sel = window.getSelection();
    const text = sel?.toString().trim();
    if (!text) return;
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const y = getRelativeY(rect, pageNumber);
    setSelection({ text, page: pageNumber, y });
  };

  const handleLineClick = (e, pageNumber) => {
    const sel = window.getSelection();
    if (sel && sel.toString().trim()) return;

    const target = e.target;
    if (target.tagName === 'SPAN' && target.closest('.react-pdf__Page__textContent')) {
      const text = target.textContent.trim();
      if (!text) return;
      const rect = target.getBoundingClientRect();
      const y = getRelativeY(rect, pageNumber);
      setSelection({ text, page: pageNumber, y });
    }
  };

  const submitComment = async () => {
    if (!commentDraft.trim() || !selection) return;
    await api.post('/manual-guide/manual-comments/', {
      manual: manual.id,
      page_number: selection.page,
      quoted_text: selection.text,
      content: commentDraft,
      position_y: selection.y,
    });
    setCommentDraft('');
    setSelection(null);
    fetchComments(manual.id);
  };

  const handleUpload = async (file, updateNote) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', '팀장 사용설명서');
    formData.append('update_note', updateNote);
    await api.post('/manual-guide/manuals/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setShowUploadModal(false);
    fetchCurrentManual();
  };

  const handleDownload = () => {
    window.open(`/api/manual-guide/manuals/${manual.id}/download/`, '_blank');
  };

  // 원본의 마커를 클릭 → 사이드바의 해당 댓글로 스크롤 + 강조
  const scrollToComment = (id) => {
    setHighlightId(id);
    const el = commentRefs.current[id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  if (!manual) {
    return (
      <div style={{ padding: 24 }}>
        <p>등록된 사용설명서가 없습니다.</p>
        {isTeamLeader && (
          <button
            onClick={() => setShowUploadModal(true)}
            style={{ width: 'auto', display: 'inline-block', padding: '8px 16px' }}
          >
            사용설명서 등록
          </button>
        )}
        {showUploadModal && (
          <UploadModal onClose={() => setShowUploadModal(false)} onUpload={handleUpload} />
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: 12 }}>
      <style>{lineHoverStyle}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3>{manual.title} (v{manual.version})</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleDownload} style={{ width: 'auto', padding: '6px 12px' }}>
            PDF 다운로드
          </button>
          {isTeamLeader && (
            <button
              onClick={() => setShowUploadModal(true)}
              style={{ width: 'auto', padding: '6px 12px' }}
            >
              업데이트
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        {/* 원본 PDF: 그대로, 문장 왼쪽에 댓글 있으면 가는 마커만 표시 */}
        <div ref={scrollContainerRef} style={{ position: 'relative' }}>
          <Document
            file={manual.file}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          >
            {Array.from({ length: numPages }, (_, i) => {
              const pageNumber = i + 1;
              const pageComments = comments.filter((c) => c.page_number === pageNumber);

              return (
                <div
                  key={i}
                  ref={(el) => { pageWrapperRefs.current[pageNumber] = el; }}
                  style={{ position: 'relative', marginBottom: 32 }}
                >
                  <div
                    onMouseUp={() => handleTextSelect(pageNumber)}
                    onClick={(e) => handleLineClick(e, pageNumber)}
                  >
                    <Page pageNumber={pageNumber} renderTextLayer={true} width={PAGE_WIDTH} />
                  </div>

                  {/* 댓글 달린 줄 표시용 왼쪽 가는 마커 (텍스트를 가리지 않음) */}
                  {pageComments.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => scrollToComment(c.id)}
                      title={`${c.author_name}: ${c.content}`}
                      style={{
                        position: 'absolute',
                        left: -10,
                        top: c.position_y ?? 0,
                        width: 6,
                        height: 18,
                        borderRadius: 3,
                        background: highlightId === c.id ? '#ff7a45' : '#1677ff',
                        cursor: 'pointer',
                      }}
                    />
                  ))}

                  {/* 새 댓글 입력창: 선택한 줄 바로 아래에 표시 (기존 텍스트 안 가림, 여백에만 겹침) */}
                  {selection && selection.page === pageNumber && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: selection.y + 20,
                        width: PAGE_WIDTH,
                        background: '#fffbe6',
                        border: '1px solid #ffe58f',
                        borderRadius: 4,
                        padding: 10,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 10,
                      }}
                    >
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                        "{selection.text.slice(0, 60)}{selection.text.length > 60 ? '...' : ''}"
                      </div>
                      <input
                        value={commentDraft}
                        onChange={(e) => setCommentDraft(e.target.value)}
                        placeholder="댓글 입력"
                        autoFocus
                        style={{ width: '70%' }}
                      />
                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        <button onClick={submitComment} style={{ width: 'auto', padding: '4px 10px' }}>
                          등록
                        </button>
                        <button onClick={() => setSelection(null)} style={{ width: 'auto', padding: '4px 10px' }}>
                          취소
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </Document>
        </div>

        {/* 댓글 사이드바: 원본과 같은 세로 위치에 정렬 */}
        <div style={{ position: 'relative', flex: 1, minWidth: 260 }}>
          <h4 style={{ marginTop: 0 }}>댓글 ({comments.length})</h4>
          {comments.map((c) => {
            const top = (pageTopOffsets[c.page_number] ?? 0) + (c.position_y ?? 0);
            return (
              <div
                key={c.id}
                ref={(el) => { commentRefs.current[c.id] = el; }}
                onClick={() => setHighlightId(c.id)}
                title={`"${c.quoted_text}"`}
                style={{
                  position: 'absolute',
                  top,
                  left: 0,
                  right: 0,
                  padding: '10px 14px',
                  background: highlightId === c.id ? '#fff2e8' : '#f7f8fa',
                  border: `1px solid ${highlightId === c.id ? '#ff7a45' : '#e8e8e8'}`,
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: 15 }}>
                  <strong>{c.author_name}</strong> · {c.content}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showUploadModal && (
        <UploadModal onClose={() => setShowUploadModal(false)} onUpload={handleUpload} />
      )}
    </div>
  );
}

function UploadModal({ onClose, onUpload }) {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState('');

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
    }}>
      <div style={{ background: '#fff', padding: 24, width: 400 }}>
        <h4>사용설명서 업데이트</h4>
        <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0])} />
        <textarea
          placeholder="업데이트 요약 (예: 3장 리더십 파트 보완)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          style={{ width: '100%', marginTop: 8 }}
        />
        <div style={{ marginTop: 12, textAlign: 'right' }}>
          <button onClick={onClose} style={{ width: 'auto', padding: '6px 14px' }}>
            취소
          </button>
          <button
            onClick={() => file && onUpload(file, note)}
            style={{ width: 'auto', padding: '6px 14px', marginLeft: 8 }}
          >
            업로드
          </button>
        </div>
      </div>
    </div>
  );
}